/**
 * Workspace mode -- the full-bleed shell a document editor asks the page for.
 *
 * This is the recipe already shipped on the single-page base, lifted into one place so the two
 * page bases cannot drift into two mechanisms (the owner's rule is one workspace modifier, not a
 * third). It is DATA plus two tiny helpers, not a component: the page base owns its own render
 * tree, and a component here would have to own the hero, the ad slots and the SEO body, which is
 * exactly the coupling the no-shared-core ruling forbids.
 *
 * The four facts, as re-measured on `components/single-page-converters/single-page-base.component.tsx`
 * at 257e3528 (the brief's line numbers had drifted; P5b pre-flight row 58):
 *   1. `<MotiveFrame hideSidePanes={this.workspaceActive}>`                       (:480)
 *   2. the shell's wrapper takes `maxWidth: active ? '100%' : '1200px'`            (:484)
 *   3. the page-top ad slot is not rendered while active                          (:485-493)
 *   4. `active` is DERIVED from "the editor that asked for it is actually mounted",
 *      never read straight off state -- a page showing a workspace chrome with no
 *      editor under it reads as broken            (the getter at :443-448, reset at :414)
 *
 * Rule 4 is why `isWorkspaceActive` takes both halves rather than one boolean.
 *
 * STATUS, stated plainly: this IS the source, as of P5c.1a (2026-09-17). Both page bases call
 * these helpers -- `single-page-base.component.tsx` (the /pdf-editor workspace) and
 * `converter-page-base.component.tsx` (the Studio tab) -- and neither carries the literal any
 * more. It was a MIRROR for the length of Phase 5b, which is what the old note here said; the
 * guard that replaced it reads BOTH shipped page bases and fails if either one inlines the
 * recipe again (`workspace-mode.spec.ts`, last case).
 */

export interface WorkspaceModeInput {
    /** The page asked for workspace mode (the feature opted in). */
    requested: boolean;
    /** The editor that asked for it is mounted and live right now. */
    editorMounted: boolean;
}

export const WORKSPACE_MAX_WIDTH = '1200px';

export function isWorkspaceActive(input: WorkspaceModeInput): boolean {
    return input.requested && input.editorMounted;
}

/** The wrapper style both page bases apply to the element inside `MotiveFrame`. */
export function workspaceWrapperStyle(active: boolean): { width: string; maxWidth: string } {
    return { width: '100%', maxWidth: active ? '100%' : WORKSPACE_MAX_WIDTH };
}

/**
 * The FITTED workspace -- what the Studio asks for on top of workspace mode (STUDIO_FIX_PROMPT.md
 * S-02, R1). Workspace mode widens the card and hides the marketing slots; the fitted editor also
 * needs the vertical budget: the page wrapper's 40 px of padding and its `min-height: 100vh`, and
 * the card's padding, border and shadow, are all space a viewport-high editor cannot spend (the
 * QA build lost ~300 px above the Studio header to them plus the H1/subtitle/tab strip). The
 * shell is then the surface: it starts right under the site nav and draws its own cards.
 *
 * Two helpers rather than a flag on `workspaceWrapperStyle`, because the /pdf-editor workspace
 * keeps the card as it is -- the Studio (R1) and, since the picker-fix wave (F2, finding Q10),
 * the Frame picker are the fitted editors.
 *
 * Inline styles, not a class: the SCSS module belongs to the page base and the closed-tab render
 * must stay byte-identical (§0c condition 2) -- an inline style that is `undefined` while closed
 * is exactly how `workspaceWrapperStyle` already achieves that.
 */
export function fittedWrapperStyle(): { padding: number; minHeight: number } {
    return { padding: 0, minHeight: 0 };
}

export function fittedCardStyle(): {
    width: string; maxWidth: string; padding: number; border: number; borderRadius: number; boxShadow: string;
} {
    return { ...workspaceWrapperStyle(true), padding: 0, border: 0, borderRadius: 0, boxShadow: 'none' };
}

/**
 * Whether a marketing slot renders. Named for what it decides rather than what it is, so a call
 * site reads `{showPageFurniture(active) && <MotiveComponent …/>}` -- the same shape as the
 * shipped `{!this.workspaceActive && (…)}` but with the negation in one place.
 */
export function showPageFurniture(active: boolean): boolean {
    return !active;
}

/**
 * The class the page body carries while a workspace is open, so the SEO body can be hidden the
 * way `.file-selected` already hides it rather than by a second rule.
 */
export const WORKSPACE_BODY_CLASS = 'workspace-open';

/**
 * The fitted editor's TOP OFFSET, published as a CSS variable on the fitted element itself so its
 * stylesheet can bound the editor to `100dvh` minus it. The Studio does this with its own name
 * (`--xc-studio-top`, `components/studio/studio-skeleton.component.tsx`); the picker's page-base
 * wrapper does it with this one (picker-fix wave F2, finding Q10), and the helper lives here
 * because the page base owns the wrapper and may not import from either editor's tree.
 *
 * WHAT IT HOLDS for the PICKER, since round 3 (ROUND3_BRIEF.md R8): ZERO. (The Studio's is MEASURED again
 * since round 13b -- `measureFittedTop` below, N4.) The first version measured the
 * element's distance from the document top (the site nav, 65 px) and the editor was sized
 * `100dvh - nav - anchor`, so `scrollHeight === innerHeight` at every viewport (p0 probe) and the
 * nav -- `position: relative`, never sticky -- could not scroll away: it READ as sticky. Now the
 * editor claims the whole viewport (`100dvh - anchor`), the page is nav + editor, and
 * `claimViewport` scrolls the nav off ONCE when the tab is activated. On a DESKTOP or tablet the
 * user can scroll back up to the nav; nothing pins it. On a PHONE (round 9, L2; R8 QA #2) the
 * Studio's shell pins ITSELF -- `position: fixed; inset: 0` under `body.xc-fullscreen` from its first
 * paint (`components/studio/views/shell.component.tsx` `pinPhone`) -- because a body-scrolled phone
 * page let a pull-down bring the nav back over the editor and a swipe scroll the editor away; the
 * `⋯` menu's "Exit fit to screen" releases it, which is how the page body stays reachable there.
 *
 * The CSS fallback stays the nav alone (65 px, measured on the 1440x900 QA build with the page
 * furniture removed): it is what shows when scripting has failed, and with no script there is no
 * scroll either, so nav + `100dvh - 65px` is exactly the page that fits.
 */
export const PICKER_TOP_VAR = '--xc-picker-top';
export const FITTED_TOP_FALLBACK_PX = 65;
/** The picker's published value: the editor starts at the top of the viewport once `claimViewport` has run. */
export const FITTED_TOP_PX = 0;

/**
 * Round 13b (R13b-ZZ1, R13 QA studio N4): the VISIBLE height of whatever stands above `el` right now -- its
 * own top edge in viewport coordinates, floored at 0. With the editor unpinned at rest (R13-Z2) and the top
 * published as 0, the shell was a full `100dvh` while the 65/69 px nav was still on screen, so its last
 * 65 px sat below the fold: the desktop timeline's Music lane at y 900 in a 900 viewport, 63 px of every
 * phone pane under the tab bar. Measured from the element itself, the value is the nav while the page is at
 * rest, shrinks as the user scrolls the nav off, and is 0 once the editor is docked (the snap), pinned
 * (`position: fixed`) or fullscreen -- so the box always ends at the fold. `FITTED_TOP_PX` when there is no
 * layout to read (SSR, jsdom, a detached node).
 */
export function measureFittedTop(el: HTMLElement | null): number {
    if (!el || typeof window === 'undefined') return FITTED_TOP_PX;
    try {
        const top = el.getBoundingClientRect().top;
        return Number.isFinite(top) ? Math.max(0, Math.round(top)) : FITTED_TOP_PX;
    } catch {
        return FITTED_TOP_PX;
    }
}

/**
 * The width from which the PICKER is fitted (`.picker-fitted`'s media query in
 * converter-page-base.component.module.scss; the kit's `$fp-bp-desktop`). Below it the picker page
 * flows normally and nothing here scrolls. The Studio is fitted at every width (S-06) and passes
 * `{ always: true }`.
 */
export const FITTED_MIN_WIDTH_PX = 1200;

/** `100dvh` minus the published top offset minus the anchor bar (`--xc-anchor-h`, the anchor's own measurement). */
export function fittedHeight(topVar: string = PICKER_TOP_VAR): string {
    return `calc(100dvh - var(${topVar}, ${FITTED_TOP_FALLBACK_PX}px) - var(--xc-anchor-h, 0px))`;
}

/**
 * Write `topVar` = `${top}px` on `el` -- `0px` by default (see the header: the picker claims the viewport);
 * the Studio passes `measureFittedTop(el)` (round 13b, N4). Silent when there is no window or no element.
 * Kept as a function with the old name so both page bases and the Studio skeleton call one thing, and so a
 * spec can pin the value.
 */
export function publishFittedTop(el: HTMLElement | null, topVar: string = PICKER_TOP_VAR, top: number = FITTED_TOP_PX): void {
    if (!el || typeof window === 'undefined') return;
    try {
        const value = `${top}px`;
        // Round 13b: re-read on every page scroll, so an unchanged value is not re-written (no style churn).
        if (el.style.getPropertyValue(topVar) !== value) el.style.setProperty(topVar, value);
    } catch {
        // a detached node: the CSS fallback stands.
    }
}

/** Is the viewport at or past the picker's fitted line? False during SSR. */
export function isFittedViewport(win: Pick<Window, 'innerWidth'> | null = typeof window === 'undefined' ? null : window): boolean {
    return !!win && win.innerWidth >= FITTED_MIN_WIDTH_PX;
}

/**
 * The element whose scroll position moves `el` on screen: the nearest ancestor that is a real
 * scroller (`overflow-y: auto|scroll` AND taller content than box), or null for the viewport.
 *
 * This site is BODY-scrolled: `styles/globals.scss` gives `html` `overflow-x: hidden` and `body`
 * `height: 100%; overflow-y: scroll`, so `body`'s overflow does not propagate to the viewport and
 * `window.scrollY` stays 0 while the page moves (the p0 probe read `scrollY: 0` with the nav at
 * `y: -416`). `window.scrollTo` alone would therefore do nothing; the scroller has to be found.
 */
export function scrollContainerOf(el: HTMLElement): HTMLElement | null {
    if (typeof window === 'undefined') return null;
    let node: HTMLElement | null = el.parentElement;
    while (node && node !== document.documentElement) {
        let overflowY = '';
        try { overflowY = window.getComputedStyle(node).overflowY; } catch { /* jsdom without styles */ }
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
        node = node.parentElement;
    }
    return null;
}

/**
 * Round 13 (R13-Z2, owner review 5 S2/P6; the ruling in ROUND13_BRIEF §0): NO PROGRAMMATIC SCROLL when an
 * editor tab opens, loads or is restored -- the page keeps its header (Core Web Vitals: a deep link
 * `?tab=studio&sid=…` must not jump). Instead the PAGE SCROLLER snaps: while a fitted editor is open the
 * body (this site's scroll container, see `scrollContainerOf`) carries this class, the page base's
 * stylesheet gives it `scroll-snap-type: y proximity`, and the editor's box is `scroll-snap-align: start`
 * -- so the editor "snaps into view for a moment" as the user scrolls past its top edge, and scrolls
 * freely everywhere else. The class is the page base's to set (both editors mount inside it); a probe
 * reads it off `<body>`.
 */
export const EDITOR_SNAP_BODY_CLASS = 'xc-editor-snap';

/** What the body scroller gets while a fitted editor is open (the editor's box is the `start`-aligned snap area). */
export const EDITOR_SNAP_TYPE = 'y proximity';

/** The body's `scroll-snap-type` before an editor wrote its own, put back when the last editor closes. */
let snapTypeBefore: string | null = null;
/** Undo for the one-time "first user scroll" listeners while the snap is armed but not yet written. */
let disarmSnap: (() => void) | null = null;

/**
 * Put the snap class on the body while a fitted editor is open, and `scroll-snap-type` AFTER THE USER'S
 * FIRST SCROLL; take both off when no editor is open.
 *
 * Deferred, MEASURED: Chrome re-snaps a scroller whenever its snap data changes, `proximity` included --
 * with the type written on activation the body landed at scrollTop 65/69 (the nav's height) at 1440,
 * 768 and 390 the moment the tab opened (`s2-probe.json`, 2026-09-22): the very jump S2 forbids, only
 * done by the browser instead of us. Armed on the first `scroll` the body sees, the type lands while the
 * user is already scrolling, and the re-snap it triggers IS the "snap into view for a moment" the owner
 * asked for. The type is written on the body's own style -- as `containBodyOverscroll` writes
 * `overscroll-behavior` (timeline-lane.component.ts) -- because a CSS module cannot carry a pure
 * `:global(body…)` rule and `globals.scss` is the site's file. Whatever value the page had is restored,
 * never blindly `''`. Silent during SSR.
 */
export function syncEditorSnap(on: boolean, body: HTMLElement | null = typeof document === 'undefined' ? null : document.body): void {
    if (!body) return;
    const has = body.classList.contains(EDITOR_SNAP_BODY_CLASS);
    if (on && !has) {
        snapTypeBefore = body.style.scrollSnapType;
        body.classList.add(EDITOR_SNAP_BODY_CLASS);
        const arm = () => {
            disarmSnap?.();
            body.style.scrollSnapType = EDITOR_SNAP_TYPE;
        };
        body.addEventListener('scroll', arm, { passive: true });
        if (typeof window !== 'undefined') window.addEventListener('scroll', arm, { passive: true });
        disarmSnap = () => {
            body.removeEventListener('scroll', arm);
            if (typeof window !== 'undefined') window.removeEventListener('scroll', arm);
            disarmSnap = null;
        };
    } else if (!on && has) {
        disarmSnap?.();
        body.style.scrollSnapType = snapTypeBefore ?? '';
        snapTypeBefore = null;
        body.classList.remove(EDITOR_SNAP_BODY_CLASS);
    }
}

export interface ClaimViewportOptions {
    /**
     * Scroll at every width. Since round 6 (R6-I5, H6 UX#23) this is the ONLY behaviour -- the picker
     * used to claim the viewport from `FITTED_MIN_WIDTH_PX` only, so on a phone it sat under the site
     * nav while the Studio hid it; both editors now scroll the nav off at every width. Kept as an
     * accepted, no-op option so the Studio's call site needs no change.
     */
    always?: boolean;
}

/**
 * Scroll ONCE so `el`'s top edge sits at the top of its scroller -- the site nav above it scrolls
 * off and the fitted editor fills the screen (R8). Returns the target offset, or null when nothing
 * was scrolled (SSR or no element).
 *
 * Round 13 (R13-Z2, S2/P6): NOT run on tab activation any more -- the owner: "don't auto scroll"
 * (CWV; the deep link jumped). The page bases no longer call it; the one caller left is the Studio
 * shell's EXPLICIT "Fit to screen" pin (`shell.component.tsx` `fallback`), which needs the nav off
 * screen before the root turns fixed (R9 QA N1). Instant (`behavior: 'auto'`): a smooth scroll under a
 * layout that is still settling reads as a jump anyway, and the anchor bar's `--xc-anchor-h` is on
 * `<html>`, unaffected by where the body is scrolled to.
 */
export function claimViewport(el: HTMLElement | null, _options: ClaimViewportOptions = {}): number | null {
    if (!el || typeof window === 'undefined') return null;
    try {
        const scroller = scrollContainerOf(el);
        const elTop = el.getBoundingClientRect().top;
        if (scroller) {
            const top = Math.max(0, Math.round(scroller.scrollTop + elTop - scroller.getBoundingClientRect().top));
            if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top, behavior: 'auto' });
            else scroller.scrollTop = top;
            return top;
        }
        const top = Math.max(0, Math.round((window.scrollY || 0) + elTop));
        window.scrollTo({ top, behavior: 'auto' });
        return top;
    } catch {
        // jsdom without layout, or a detached node: nothing to scroll.
        return null;
    }
}

/**
 * WHEN THE EDITOR OWNS THE VIEWPORT (R1 round 1 §D, 2026-09-22 — the reviewer's ruling).
 *
 * Here, and not in the editor, because BOTH ends need the same answer and neither may own it:
 * `components/pdf-editor/views/mobile-sheet.tsx` re-exports these for the editor's own chrome,
 * and the page bases below read them to decide whether to render a marketing page underneath an
 * open document. A page base importing an editor VIEW to ask the question would pull the whole
 * editor tree into the statically-imported shell, which is the one thing the mount gate exists to
 * prevent — so the predicate lives in the leaf both sides already depend on.
 *
 * The editor has never owned a scroll region: its pane declares `overflow: auto` and nothing
 * bounds its height, so the nearest real scrollport is `<html>`. R1 measured the cost at 390x844:
 * `document.scrollingElement.scrollHeight` 25 344 px against a 450 px document, and one 1 520 px
 * flick leaving nothing on screen but the "How to Edit a PDF Online" article — no rail, no sheet,
 * no Save, and no way back except scrolling up 1 500 px. Both alternatives were measured and both
 * fail; only owning the viewport removes the class.
 *
 * It is deliberately NOT the editor's layout breakpoint (767 px wide / 500 px tall, "does the
 * chrome still fit side by side"). This asks whether a finger can flick the page away, and a
 * 900 px tablet in portrait has room for the desktop layout and still has the defect.
 * `(pointer: coarse)` rather than `(hover: none)`, because a Surface has both a touch screen and
 * a mouse and the defect is a property of the finger being available.
 */
export const VIEWPORT_OWNED_MAX_WIDTH = 900;

/** The one media query the stylesheet's `position: fixed; inset: 0` block is keyed on. Two
 *  spellings of one condition is how a layout and the behaviour it implies drift apart. */
export const VIEWPORT_OWNED_MEDIA_QUERY =
    `(max-width: ${VIEWPORT_OWNED_MAX_WIDTH}px), (pointer: coarse)`;

/**
 * The runtime half. `false` on the server and wherever `matchMedia` is missing: the desktop
 * layout is the one that works without the change, so an unknown environment gets it — and a
 * server render therefore emits the full marketing page, which is what keeps the static output
 * crawlable (see `showPageChromeAroundEditor`).
 */
export function editorOwnsViewport(): boolean {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia === 'function') {
        try { return window.matchMedia(VIEWPORT_OWNED_MEDIA_QUERY).matches; } catch { /* below */ }
    }
    // `matchMedia` absent (jsdom without a stub, very old Safari): the width half is still
    // answerable and is the half that covers every phone and tablet in portrait.
    return window.innerWidth <= VIEWPORT_OWNED_MAX_WIDTH;
}

/**
 * DOES THE PAGE DRAW ANYTHING OF ITS OWN AROUND AN OPEN DOCUMENT? (R1 round 1 §D item 4; Q4 §2.)
 *
 * BOTH SIDES OF THE EDITOR, which is why it is not named "below" any more. §D item 4 was about
 * the article and the footer underneath; Q4 §2 measured the other side and it is worse — at cold
 * load `[data-testid="page-stack"]`'s top edge sits at **`y = 321.19 px`, identical at 360x780 L,
 * 390x844 L and 412x915 L**, so the page area is 10.8 % / 17.6 % / 22.0 % of the viewport and
 * **zero text runs are visible at any of the three**. None of that band collapses for a
 * height-constrained viewport. The part of it this page base owns is its own: the file chip that
 * replaces the hero, and the card and row padding around it.
 *
 * No — on a surface where the editor owns the viewport. R1's own words: the article and the
 * footer "stay on the pre-upload gate, which is the state a crawler sees, so nothing is lost".
 * This is the piece that makes `position: fixed; inset: 0` honest rather than something with a
 * 25 344 px page still standing behind it, and it is what makes the wave's integration assertion
 * — "no `position: fixed/sticky` element with a non-editor element behind its centre point" —
 * satisfiable at all: today the thing behind the zoom pill's centre after a flick IS the help
 * article.
 *
 * TWO INPUTS, AND THE SEO CONSEQUENCE IS THE REASON. `active` is already "a document is open in
 * a mounted editor" and is set by a client event, so this can only ever go false AFTER a user
 * has chosen a file. A crawler, and the static export, see `active === false` and
 * `editorOwnsViewport() === false` (no `window` on the server), so `/pdf-editor` still ships and
 * still renders the whole article, the related links, the rating and the tiles. Nothing is
 * removed from the page; a surface that has given its whole viewport to a document stops drawing
 * a page underneath it.
 *
 * Desktop is untouched by construction: `ownsViewport` is false with a mouse above 900 px, so the
 * marketing page stays exactly where it is with a document open, which is where the revenue
 * surfaces live.
 */
export function showPageChromeAroundEditor(active: boolean, ownsViewport: boolean): boolean {
    return !(active && ownsViewport);
}

/**
 * THE CHROME ABOVE AN OPEN DOCUMENT, ON A DESKTOP TOO (lane H, GAP §3 #3, 2026-10-04).
 *
 * Measured at 1440x900 with a document open: page 1 started at y 330 — site header 65, the
 * wrapper's 40 px top padding, the card's 32 px, the "Edit PDF Online" chip (49 px), the document
 * bar and the tool bar — 37 % of the screen before any page; Smallpdf starts the page at 217,
 * Adobe at ~90. The acceptance is the first canvas at y <= 200.
 *
 * These three answer only for `active` (a document open in a mounted editor — a client event),
 * so the gate, the static export and every crawler render exactly what they did: the hero, the
 * padding, the article. Nothing that carries an ad changes: the top slot and the rails already
 * leave with the document (`showPageFurniture`, `hideSidePanes`), and the slots below the editor
 * are untouched.
 *
 * - The tool-name chip goes at every width. With a document open it repeats the page's own name
 *   in a row of its own; the editor's document bar already names the FILE, which is what the user
 *   is working on. (It already went on a viewport-owning surface, Q4 §2.)
 * - The wrapper keeps 8 px of its 40 px top padding — the card still reads as a card under the
 *   header — and the card drops its 32 px top padding so the document bar is the card's top row.
 *   Sides and bottom keep a 16 px inset.
 */
export function showToolNameAboveEditor(active: boolean): boolean {
    return !active;
}

export function editorOpenWrapperStyle(active: boolean): { paddingTop: number } | undefined {
    return active ? { paddingTop: 8 } : undefined;
}

export function editorOpenCardStyle(active: boolean):
    { paddingTop: number; paddingLeft: number; paddingRight: number; paddingBottom: number } | undefined {
    return active ? { paddingTop: 0, paddingLeft: 16, paddingRight: 16, paddingBottom: 16 } : undefined;
}
